import React, { useState } from "react";
import { 
  Bookmark, 
  MapPin, 
  DollarSign, 
  TrendingUp, 
  Eye, 
  ArrowRight, 
  Play,
  BarChart3,
  Trash2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setTargetJob, setTargetJobDescription } from "../../redux/userSlice";
import { api1 } from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../services/apiEndpoints";
import { COLORS } from "../../constants/colors";
import { toast } from "react-toastify";

const CareerBookmarkCard = ({ 
  career, 
  onRemove, 
  showRemoveButton = true,
  compact = false 
}) => {
  const [isBookmarking, setIsBookmarking] = useState(false);
  const [showAllSkills, setShowAllSkills] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showCareerSelectedModal, setShowCareerSelectedModal] = useState(false);
  const [isSelectingCareer, setIsSelectingCareer] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const targetJob = useSelector((state) => state.user.targetJob);

  const isCareerSelected = () => {
    const careerName = career.title || career.careerName;
    return targetJob && careerName && targetJob.toLowerCase() === careerName.toLowerCase();
  };

  const handleRemoveBookmark = async () => {
    if (!career._id) return;
    
    try {
      setIsBookmarking(true);
      const careerType = career.careerType || career.category;
      const bookmarkData = {
        userId: career.userId,
        careerName: career.careerName || career.title,
        careerType
      };
      
      await api1.post(API_ENDPOINTS.UNSAVE_CAREER_BOOKMARK, bookmarkData);
      onRemove?.(career._id);
      toast.success("Career removed from saved list");
    } catch (error) {
      console.error("Error removing bookmark:", error);
      toast.error("Failed to remove career");
    } finally {
      setIsBookmarking(false);
    }
  };

  const handleViewDetails = () => {
    navigate(`/dashboard/career-discovery/career-recommendations?career=${career.careerId}`);
  };

  const handleSelectCareer = async () => {
    setIsSelectingCareer(true);
    const targetJobAction = setTargetJob(career.title || career.careerName);
    const targetJobDescAction = setTargetJobDescription(career.description);
    dispatch(targetJobAction);
    dispatch(targetJobDescAction);
    
    const targetCareerType = career.careerType || career.category;
    
    try {
      await api1.patch(`${API_ENDPOINTS.UPDATE_PROFILE}/${career.userId}`, { 
        targetJob: career.title || career.careerName, 
        targetJobDescription: career.description,
        targetCareerType
      });
      
      setShowCareerSelectedModal(true);
    } catch (err) {
      console.error("Selection error:", err);
      toast.error("Failed to select career");
    } finally {
      setIsSelectingCareer(false);
    }
  };

  const handleSimulateCareer = () => {
    navigate("/dashboard/career-discovery/career-simulation-tool");
  };

  const handleConductSkillsGap = () => {
    navigate("/dashboard/skills-and-learning/skill-gap-analysis-tool");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'numeric', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getCategoryColor = (category) => {
    return category === "emerging" ? COLORS.purple : "#3b82f6";
  };

  const getCategoryBgColor = (category) => {
    return category === "emerging" ? COLORS.purpleLight : "#eff6ff";
  };

  if (compact) {
    return (
      <div className="bg-white rounded-lg shadow-md border border-gray-400 p-4 hover:shadow-lg transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                career.category === "emerging" || career.careerType === "emerging"
                  ? "text-white"
                  : "text-gray-700 border border-gray-300"
              }`}
              style={{ 
                backgroundColor: career.category === "emerging" || career.careerType === "emerging" 
                  ? '#6e28cc' 
                  : 'white' 
              }}
            >
              {career.category === "emerging" || career.careerType === "emerging" ? "Emerging" : "Mainstream"}
            </span>
            {career.savedDate && (
              <span className="text-xs text-gray-500">
                Saved {formatDate(career.savedDate)}
              </span>
            )}
          </div>
          {showRemoveButton && (
            <button
              onClick={handleRemoveBookmark}
              disabled={isBookmarking}
              className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
          {career.title}
        </h3>
        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
          {career.description}
        </p>

        <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
          {career.location && (
            <div className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              <span>{career.location}</span>
            </div>
          )}
          {career.salaryRange && (
            <div className="flex items-center gap-1">
              <DollarSign className="w-3 h-3" />
              <span>{career.salaryRange}</span>
            </div>
          )}
          {career.growthPercentage && (
            <div className="flex items-center gap-1 text-green-600">
              <TrendingUp className="w-3 h-3" />
              <span>{career.growthPercentage}</span>
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleViewDetails}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Eye className="w-3 h-3" />
            View
          </button>
          <button
            onClick={handleSelectCareer}
            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-white text-gray-700 text-xs font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            Select
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Career Selected Modal */}
      {showCareerSelectedModal && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md relative">
            <h2 className="text-xl font-bold mb-4 text-center">Your Target Career has been Selected</h2>
            <p className="text-sm text-gray-700 mb-6 text-center">
              You have selected '{career.title || career.careerName}' as your target career. What would you like to do next?
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setShowCareerSelectedModal(false);
                  navigate("/dashboard/career-assessment/ai-literacy-assessment");
                }}
                className="w-full py-2 px-4 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Start AI Literacy Assessment
              </button>
              <button
                onClick={() => {
                  setShowCareerSelectedModal(false);
                  navigate("/dashboard/skills-and-learning/upskilling-pathway-config");
                }}
                className="w-full py-2 px-4 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Skip Assessment and Generate Upskilling Pathway <span className="text-red-500">(Not Advisable)</span>
              </button>
              <button
                onClick={() => setShowCareerSelectedModal(false)}
                className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-400 p-6 hover:shadow-md transition-shadow">
      {/* Top Row - Tags and Delete Button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          {/* Career Type Tag */}
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              career.category === "emerging" || career.careerType === "emerging"
                ? "text-white"
                : "text-gray-700 border border-gray-300"
            }`}
            style={{ 
              backgroundColor: career.category === "emerging" || career.careerType === "emerging" 
                ? '#6e28cc' 
                : 'white' 
            }}
          >
            {career.category === "emerging" || career.careerType === "emerging" ? "Emerging" : "Mainstream"}
          </span>
          {/* White Saved Date Tag */}
          {career.savedDate && (
            <span className="px-2 py-1 rounded-full text-xs font-medium text-gray-700 border border-gray-300 bg-white">
              Saved {formatDate(career.savedDate)}
            </span>
          )}
        </div>
        {showRemoveButton && (
          <button
            onClick={handleRemoveBookmark}
            disabled={isBookmarking}
            className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Job Title - Large Dark Blue */}
      <h3 className="text-xl font-bold mb-2" style={{ color: '#1e3a8a' }}>
        {career.title || career.careerName}
      </h3>

      {/* Job Description - Gray Text */}
      <div className="mb-4">
        <p 
          className={`text-gray-600 leading-relaxed ${
            !showFullDescription ? 'overflow-hidden' : ''
          }`}
          style={{
            display: !showFullDescription ? '-webkit-box' : 'block',
            WebkitLineClamp: !showFullDescription ? 2 : 'unset',
            WebkitBoxOrient: 'vertical',
            maxHeight: !showFullDescription ? '3.6rem' : 'none'
          }}
        >
          {career.description}
        </p>
        {career.description && career.description.length > 100 && (
          <button
            onClick={() => setShowFullDescription(!showFullDescription)}
            className="text-blue-600 text-sm font-medium hover:text-blue-700 transition-colors mt-1"
          >
            {showFullDescription ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>

      {/* Key Skills Section */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Key Skills</h4>
        <div className="flex flex-wrap gap-2">
          {(career.skills || career.keySkills || [])
            .slice(0, showAllSkills ? undefined : 3)
            .map((skill, index) => (
              <span
                key={index}
                className="px-2 py-1 bg-white text-gray-700 text-xs rounded-full border border-gray-400"
              >
                {skill}
              </span>
            ))}
          {(career.skills || career.keySkills || []).length > 3 && !showAllSkills && (
            <button
              onClick={() => setShowAllSkills(true)}
              className="px-3 py-1 bg-white text-gray-700 text-xs rounded-full border border-gray-400 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              +{(career.skills || career.keySkills || []).length - 3} more
            </button>
          )}
          {showAllSkills && (career.skills || career.keySkills || []).length > 3 && (
            <button
              onClick={() => setShowAllSkills(false)}
              className="px-3 py-1 bg-white text-gray-700 text-xs rounded-full border border-gray-400 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              Show less
            </button>
          )}
        </div>
      </div>

      {/* Action Buttons - 2x2 Grid */}
      <div className="grid grid-cols-2 gap-3">
        {/* Top Row */}
        <button
          onClick={handleViewDetails}
          className="flex items-center justify-center gap-2 px-3 py-1 bg-white text-blue-600 text-sm font-medium rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors"
        >
          <Eye className="w-4 h-4" />
          View Details
        </button>
        <button
          onClick={isCareerSelected() ? null : handleSelectCareer}
          disabled={isSelectingCareer || isCareerSelected()}
          className="flex items-center justify-center gap-2 px-3 py-1 bg-white text-blue-600 text-sm font-medium rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSelectingCareer && (
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600"></div>
          )}
          {isSelectingCareer ? "Selecting..." : isCareerSelected() ? "Selected" : "Select Career"}
          {!isSelectingCareer && !isCareerSelected() && <ArrowRight className="w-4 h-4" />}
        </button>
        
        {/* Bottom Row */}
        <button
          onClick={handleSimulateCareer}
          className="flex items-center justify-center gap-2 px-3 py-1 bg-white text-blue-600 text-sm font-medium rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors"
        >
          <Play className="w-4 h-4" />
          Simulate this Career
        </button>
        <button
          onClick={handleConductSkillsGap}
          className="flex items-center justify-center gap-2 px-3 py-1 bg-white text-blue-600 text-sm font-medium rounded-lg border border-blue-600 hover:bg-blue-50 transition-colors"
        >
          <BarChart3 className="w-4 h-4" />
          Conduct Skills Gap
        </button>
      </div>
      </div>
    </>
  );
};

export default CareerBookmarkCard;
